// Knowledge Base — author help articles that publish to Help Centres for
// customer self-serve, and that agents can search/link from a ticket.
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import RichEditor from '../../components/RichEditor';
import ServiceDeskUpsell from '../../components/serviceDesk/ServiceDeskUpsell';
import { BookOpen, Plus, MagnifyingGlass, Eye, ThumbsUp, ArrowLeft, Trash } from '@phosphor-icons/react';

const STATUS_TONE = { published: 'bg-emerald-50 text-emerald-700 ring-emerald-200', draft: 'bg-amber-50 text-amber-700 ring-amber-200' };

export default function KnowledgePage() {
  const { API, getOrgHeaders, currentOrg } = useAuth();
  const { caps } = useSubscription();
  const h = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);
  const sdEnabled = caps?.service_desk_enabled === true;

  const [articles, setArticles] = useState([]);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // full article object being edited

  const load = useCallback(async () => {
    if (!currentOrg || !sdEnabled) { setLoading(false); return; }
    setLoading(true);
    try { const { data } = await axios.get(`${API}/kb/articles`, h()); setArticles(data || []); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, [currentOrg, sdEnabled, API, h]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return articles.filter(a => {
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;
      if (q && !(`${a.title} ${a.category} ${(a.tags || []).join(' ')}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [articles, query, statusFilter]);

  const openNew = () => setEditing({ title: '', body: '', excerpt: '', category: 'General', tags: [], status: 'draft' });
  const openEdit = async (id) => { try { const { data } = await axios.get(`${API}/kb/articles/${id}`, h()); setEditing(data); } catch { toast.error('Failed to open'); } };

  if (!sdEnabled) return <ServiceDeskUpsell />;
  if (editing) return <ArticleEditor article={editing} API={API} h={h} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;

  return (
    <div className="flex-1 overflow-auto bg-zinc-50" data-testid="kb-page">
      <div className="max-w-5xl mx-auto px-8 py-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-1"><BookOpen size={14} weight="fill" /> Knowledge Base</div>
            <h1 className="text-2xl font-bold text-zinc-900 font-['Outfit']">Articles</h1>
            <p className="text-sm text-zinc-500 mt-0.5">Self-serve answers for customers and quick references for agents.</p>
          </div>
          <Button onClick={openNew} data-testid="kb-new-btn"><Plus size={14} className="mr-1.5" /> New article</Button>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search articles…" data-testid="kb-search"
              className="w-full h-9 pl-9 pr-3 rounded-md border border-zinc-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
          </div>
          {['all', 'published', 'draft'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} data-testid={`kb-filter-${s}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium ring-1 capitalize transition-colors ${statusFilter === s ? 'bg-zinc-900 text-white ring-zinc-900' : 'bg-white text-zinc-600 ring-zinc-200 hover:bg-zinc-50'}`}>{s}</button>
          ))}
        </div>

        {loading ? <div className="text-center py-16 text-zinc-400">Loading…</div>
          : filtered.length === 0 ? (
            <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center">
              <BookOpen size={36} className="mx-auto text-zinc-300 mb-3" />
              <p className="text-sm text-zinc-500 mb-4">No articles yet. Write your first help article.</p>
              <Button onClick={openNew}>New article</Button>
            </div>
          ) : (
            <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100" data-testid="kb-list">
              {filtered.map(a => (
                <button key={a.article_id} onClick={() => openEdit(a.article_id)} data-testid={`kb-item-${a.article_id}`}
                  className="w-full text-left px-5 py-3.5 flex items-center gap-4 hover:bg-zinc-50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-zinc-900 truncate">{a.title}</p>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ring-1 capitalize ${STATUS_TONE[a.status]}`}>{a.status}</span>
                    </div>
                    <p className="text-xs text-zinc-400 mt-0.5">{a.category}{a.excerpt ? ` · ${a.excerpt}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-zinc-400 shrink-0">
                    <span className="inline-flex items-center gap-1"><Eye size={12} /> {a.view_count || 0}</span>
                    <span className="inline-flex items-center gap-1"><ThumbsUp size={12} /> {a.helpful_yes || 0}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}

function ArticleEditor({ article, API, h, onClose, onSaved }) {
  const [form, setForm] = useState({ ...article, tags: article.tags || [] });
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  const save = async (publish) => {
    if (!form.title.trim()) { toast.error('Give the article a title'); return; }
    setSaving(true);
    const payload = { ...form, status: publish ? 'published' : (form.status || 'draft'), tags: (typeof form.tags === 'string' ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : form.tags) };
    try {
      if (form.article_id) await axios.patch(`${API}/kb/articles/${form.article_id}`, payload, h());
      else await axios.post(`${API}/kb/articles`, payload, h());
      toast.success(publish ? 'Article published' : 'Article saved');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); } finally { setSaving(false); }
  };
  const remove = async () => { if (!form.article_id || !window.confirm('Delete this article?')) return; try { await axios.delete(`${API}/kb/articles/${form.article_id}`, h()); toast.success('Deleted'); onSaved(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };

  return (
    <div className="flex-1 overflow-auto bg-zinc-50" data-testid="kb-editor">
      <div className="max-w-3xl mx-auto px-8 py-6">
        <button onClick={onClose} className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 mb-4"><ArrowLeft size={14} /> Back to articles</button>
        <Input value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="Article title" data-testid="kb-title"
          className="text-xl font-bold border-0 border-b border-zinc-200 rounded-none px-0 focus-visible:ring-0 mb-4" />
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div><Label className="text-xs">Category</Label><Input value={form.category} onChange={(e) => set({ category: e.target.value })} data-testid="kb-category" /></div>
          <div><Label className="text-xs">Tags (comma-separated)</Label><Input value={Array.isArray(form.tags) ? form.tags.join(', ') : form.tags} onChange={(e) => set({ tags: e.target.value })} data-testid="kb-tags" /></div>
        </div>
        <div className="mb-3"><Label className="text-xs">Short summary (shown in search)</Label><Input value={form.excerpt} onChange={(e) => set({ excerpt: e.target.value })} placeholder="One line that helps people find this" data-testid="kb-excerpt" /></div>
        <Label className="text-xs">Content</Label>
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden mt-1" data-testid="kb-body">
          <RichEditor value={form.body} onChange={(html) => set({ body: html })} placeholder="Write the article…" minHeight={280} />
        </div>
        <div className="flex items-center gap-2 mt-4">
          <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="kb-save-draft">Save draft</Button>
          <Button onClick={() => save(true)} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="kb-publish">{form.status === 'published' ? 'Update & keep published' : 'Publish'}</Button>
          {form.article_id && <Button variant="ghost" className="text-red-600 hover:bg-red-50 ml-auto" onClick={remove} data-testid="kb-delete"><Trash size={14} className="mr-1" /> Delete</Button>}
        </div>
      </div>
    </div>
  );
}
